import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AsyncBoundary, Card, Button, Notice, Drawer, useToast,
} from '@kollez/design-system';
import {
  useGrievances, useCreateGrievance,
  type GrievanceCategory, type GrievancePriority, type GrievanceStatus,
} from '@/lib/queries';

/**
 * /settings/privacy/grievances (DPDP §13(3), Page 63/N).
 *
 * Legally required data-fiduciary complaint channel. Every user can file
 * a grievance under one of 6 canonical categories; the SLA is 90 days by
 * statute. This page is the student's / employee's file+track surface.
 * Tenant admins review at /tenant/grievances (separate page ships next).
 *
 * The audit fork (Page 62/N) flagged this as a LEGAL priority — the API
 * exists, real data is seedable, but no page consumed it. Filing a
 * grievance today required curl.
 */

const CATEGORY_LABEL: Record<GrievanceCategory, string> = {
  unauthorized_processing: 'Data processed without permission',
  consent_violation: 'Consent violation',
  data_leak: 'Data leaked / breached',
  ai_output_error: 'AI output error (Nexie / grading)',
  discrimination: 'Discrimination or unfair treatment',
  other: 'Something else',
};

const STATUS_LABEL: Record<GrievanceStatus, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  in_review: 'In review',
  resolved: 'Resolved',
  escalated: 'Escalated',
  closed: 'Closed',
};

const PRIORITY_OPTIONS: GrievancePriority[] = ['low', 'normal', 'high', 'urgent'];

function statusTone(status: GrievanceStatus): 'success' | 'info' | 'warning' | 'danger' | 'default' {
  if (status === 'resolved' || status === 'closed') return 'success';
  if (status === 'in_review' || status === 'acknowledged') return 'info';
  if (status === 'escalated') return 'danger';
  return 'warning';
}

function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

export function GrievancesPage() {
  const toast = useToast();
  const list = useGrievances({ mine: true, sortBy: 'createdAt', sortDir: 'desc', limit: 50 });
  const create = useCreateGrievance();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    category: 'other' as GrievanceCategory,
    subject: '',
    description: '',
    priority: 'normal' as GrievancePriority,
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  async function submit() {
    setSaveError(null);
    if (form.subject.trim().length < 5) {
      setSaveError('Subject must be at least 5 characters.');
      return;
    }
    if (form.description.trim().length < 20) {
      setSaveError('Please describe the concern in at least 20 characters — this record is legally binding.');
      return;
    }
    try {
      await create.mutateAsync({
        category: form.category,
        subject: form.subject.trim(),
        description: form.description.trim(),
        priority: form.priority,
      });
      toast.show({ message: 'Grievance filed. You will be notified when a Grievance Officer acknowledges it.' });
      setOpen(false);
      setForm({ category: 'other', subject: '', description: '', priority: 'normal' });
    } catch (e) {
      setSaveError((e as Error)?.message ?? 'Could not file the grievance.');
    }
  }

  const rows = list.data?.rows ?? [];

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">
            <Link to="/settings/privacy" className="link">← Privacy</Link>
          </p>
          <h1 className="h-page">File a grievance</h1>
          <p className="subhead">
            Under DPDP §13(3), you have the right to file a written complaint about how your data is
            handled. The Grievance Officer must respond within 90 days.
          </p>
        </div>
        <div className="ph-actions">
          <Button variant="primary" onClick={() => setOpen(true)}>New grievance</Button>
        </div>
      </div>

      <Notice tone="info">
        <b>How this works.</b> File a grievance, an internal reviewer acknowledges within a few
        working days, and either resolves it or escalates to the Data Protection Officer. If you
        aren't satisfied with the resolution, you can escalate further to the Data Protection Board
        of India.
      </Notice>

      <AsyncBoundary
        kind="table"
        loading={list.isLoading}
        error={list.error}
        empty={!list.isLoading && rows.length === 0}
        emptyTitle="No grievances filed"
        emptyHint="If you believe your data has been mishandled, file a grievance above. We'll acknowledge and respond within 90 days per DPDP §13."
        onRetry={() => void list.refetch()}
      >
        <Card padding="md">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Filed</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th className="r">SLA</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => {
                  const sla = daysUntil(g.slaDeadline);
                  const slaBreached = sla !== null && sla < 0 && g.status !== 'resolved' && g.status !== 'closed';
                  return (
                    <tr key={g._id}>
                      <td className="cell-sub">
                        {new Date(g.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="cell-title">{g.subject}</td>
                      <td className="cell-sub">{CATEGORY_LABEL[g.category]}</td>
                      <td>
                        <span className={`badge ${g.priority === 'urgent' ? 'badge-danger' : g.priority === 'high' ? 'badge-warning' : ''}`}>
                          {g.priority}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-${statusTone(g.status)}`}>
                          {STATUS_LABEL[g.status]}
                        </span>
                      </td>
                      <td className="r cell-sub">
                        {sla === null
                          ? '—'
                          : slaBreached
                          ? <span className="text-ink-danger" title="90-day statutory deadline breached">Breached</span>
                          : `${sla}d left`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </AsyncBoundary>

      <Drawer open={open} onClose={() => setOpen(false)} title="File a grievance" size="lg">
        <div className="grid gap-4">
          {saveError && <Notice tone="warning">{saveError}</Notice>}

          <label className="grid gap-1.5">
            <span className="label">Category</span>
            <select
              className="select"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value as GrievanceCategory })}
            >
              {(Object.entries(CATEGORY_LABEL) as Array<[GrievanceCategory, string]>).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>

          <label className="grid gap-1.5">
            <span className="label">Subject <span className="cell-sub">· 5–200 characters</span></span>
            <input
              className="input"
              maxLength={200}
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="One-line summary of the concern"
            />
          </label>

          <label className="grid gap-1.5">
            <span className="label">
              Description <span className="cell-sub">· {form.description.length}/5000 · minimum 20</span>
            </span>
            <textarea
              className="textarea"
              rows={8}
              maxLength={5000}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What happened, when, and what data was involved. Include screenshots and reference IDs if you have them."
            />
          </label>

          <label className="grid gap-1.5">
            <span className="label">Priority</span>
            <select
              className="select"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value as GrievancePriority })}
            >
              {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>

          <Notice tone="info">
            Filing this creates a legal record. Falsifying a grievance is a violation of Kollez's
            terms of service and may be reportable under DPDP §35. Please be accurate.
          </Notice>
        </div>

        <div className="drawer-foot">
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="primary"
            className="ml-auto"
            loading={create.isPending}
            onClick={submit}
          >
            File grievance
          </Button>
        </div>
      </Drawer>
    </div>
  );
}
